import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
// import authAPI from './authAPI';
// import postAPI from './postAPI';
import * as apiConfig from './config';

// 기본 API 인스턴스 설정
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:9876/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// 요청 인터셉터 - 토큰 추가
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('token');
  console.log(`API 요청: ${config.method?.toUpperCase()} ${config.url}`);
  
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.log('인증 토큰 없음');
  }
  
  return config;
});

// 응답 인터셉터 - 데이터 추출 및 에러 처리
api.interceptors.response.use(
  (response) => {
    console.log(`API 응답 성공: ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error) => {
    // 로그인이 필요한 경우 (401)
    if (error.response && error.response.status === 401) {
      // 로컬 스토리지에서 토큰 제거
      localStorage.removeItem('token');
      
      // 오류 콘솔 출력 및 에러 상세 정보 확인
      console.error('인증 오류 발생:', error.response.data);
    }
    
    console.error(`API 오류: ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || '네트워크 오류'}`, 
                  error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// 인증 관련 API
export const authAPI = {
  // 로그인
  login: (username: string, password: string) => 
    api.post('/auth/login', { username, password }),
  
  // 회원가입
  register: (userData: any) => 
    api.post('/auth/register', userData),
  
  // 로그아웃
  logout: () => 
    api.post('/auth/logout'),
  
  // 현재 사용자 정보 조회
  getCurrentUser: () => 
    api.get('/auth/me'),
    
  // 인증 상태 확인
  checkAuth: () => {
    // 토큰이 없으면 바로 인증되지 않은 상태 반환
    const token = localStorage.getItem('token');
    if (!token) {
      return Promise.resolve({ 
        data: { 
          success: true, 
          data: { 
            isLoggedIn: false 
          } 
        } 
      });
    }
    
    // 토큰이 있으면 서버에 확인 요청
    return api.get('/auth/check');
  },

  // 프로필 정보 업데이트
  updateProfile: (userData: any) => 
    api.put('/users/profile', userData)
};

// 게시판 관련 API
export const boardAPI = {
  // 모든 게시판 목록 조회
  getBoards: () => 
    api.get('/boards'),
  
  // 특정 게시판 정보 조회 (ID 기준)
  getBoardById: (id: number) => 
    api.get(`/boards/id/${id}`),
    
  // 특정 게시판 정보 조회 (slug 기준)
  getBoard: (slug: string) => 
    api.get(`/boards/${slug}`),
  
  // 특정 게시판의 게시물 목록 조회
  getBoardPosts: (boardId: number | string, page = 1, limit = 10) => 
    api.get(`/boards/${boardId}/posts`, { params: { page, limit } })
};

// 게시물 관련 API
export const postAPI = {
  // 게시물 목록 조회
  getPosts: (page = 1, limit = 10, search = '', boardId?: number) => {
    const params: any = { page, limit };
    if (search) params.search = search;
    if (boardId) params.boardId = boardId;
    return api.get('/posts', { params });
  },

  // 가벼운 버전의 게시물 목록 조회 (내용 제외)
  getPostList: (page = 1, limit = 10, search = '', searchType=0, boardId?: number) => {
    const params: any = { page, limit };
    if (search) params.search = search;
    if (searchType) params.searchType = searchType;
    if (boardId) params.boardId = boardId;
    return api.get('/posts/list', { params });
  },

  // 특정 게시물 조회
  getPostById: (id: number) => 
    api.get(`/posts/${id}`),
  
  // 게시물 작성
  createPost: (postData: any) => {
    console.log('게시물 작성 요청 데이터:', postData);
    return api.post('/posts', postData)
      .then(response => {
        console.log('게시물 작성 응답:', response.data);
        return response;
      })
      .catch(error => {
        console.error('게시물 작성 실패:', error.response?.data || error.message);
        throw error;
      });
  },
  
  // 게시물 수정
  updatePost: (id: number, postData: any) => 
    api.put(`/posts/${id}`, postData),
  
  // 게시물 삭제
  deletePost: (id: number) => 
    api.delete(`/posts/${id}`),
    
  // 게시물 추천
  likePost: (id: number) => 
    api.post(`/posts/${id}/like`),
    
  // 게시물 추천 취소
  unlikePost: (id: number) => 
    api.delete(`/posts/${id}/like`)
};

// 랜딩 페이지 관련 API
export const landingAPI = {
  // 페이지 조회수 기록
  recordView: (pageId: string) => 
    api.post('/landing/view', { pageId }),
  
  // 링크 클릭 기록
  recordClick: (pageId: string, linkId: string) => 
    api.post('/landing/click', { pageId, linkId }),
  
  // 페이지 통계 조회 (관리자용)
  getStats: (pageId?: string) => {
    const params: any = {};
    if (pageId) params.pageId = pageId;
    return api.get('/landing/stats', { params });
  }
};

// 커스텀 페이지 관련 API
export const customPageAPI = {
  // URL로 커스텀 페이지 조회
  getCustomPageByUrl: (pageUrl: string) => 
    api.get(`/custom-pages/url/${pageUrl}`),
  
  // 모든 커스텀 페이지 목록 조회
  getAllCustomPages: () => 
    api.get('/custom-pages'),
  
  // ID로 커스텀 페이지 조회
  getCustomPageById: (id: number) => 
    api.get(`/custom-pages/${id}`),
  
  // 커스텀 페이지 생성 (관리자용)
  createCustomPage: (pageData: any) => 
    api.post('/custom-pages', pageData),
  
  // 커스텀 페이지 수정 (관리자용)
  updateCustomPage: (id: number, pageData: any) => 
    api.put(`/custom-pages/${id}`, pageData),
  
  // 커스텀 페이지 삭제 (관리자용)
  deleteCustomPage: (id: number) => 
    api.delete(`/custom-pages/${id}`)
};

// 댓글 관련 API
export const commentAPI = {
  // 게시물의 댓글 목록 조회
  getCommentsByPostId: (postId: number) => api.get(`/comments/post/${postId}`),
  
  // 댓글 작성
  createComment: (postId: number, content: string, parentId?: number, isSecret?: boolean) => {
    const requestData = { 
      post_id: postId, 
      content, 
      parent_id: parentId !== undefined ? parentId : null, 
      is_secret: isSecret !== undefined ? isSecret : null 
    };
    
    console.log('댓글 작성 요청 데이터:', JSON.stringify(requestData));
    return api.post('/comments', requestData)
    .then(response => {
      console.log('댓글 작성 성공:', response.data);
      return response;
    })
    .catch(error => {
      console.error('댓글 작성 실패:', error.response?.data || error.message);
      if (error.response?.status === 400) {
        console.error('유효성 검사 오류:', error.response.data.errors);
      }
      throw error;
    });
  },
  
  // 댓글 수정
  updateComment: (id: number, content: string, isSecret?: boolean) => 
    api.put(`/comments/${id}`, { 
      content, 
      is_secret: isSecret !== undefined ? isSecret : null 
    }),
  
  // 댓글 삭제
  deleteComment: (id: number) => api.delete(`/comments/${id}`),
  
  // 댓글 추천
  likeComment: (id: number) => api.post(`/comments/${id}/like`),
  
  // 댓글 추천 취소
  unlikeComment: (id: number) => api.delete(`/comments/${id}/like`)
};

// 사용자 관련 API
export const userAPI = {
  // 사용자 프로필 조회
  getUserProfile: (userId: number) => 
    api.get(`/users/${userId}`),
  
  // 내 프로필 수정
  updateMyProfile: (userData: any) => 
    api.put('/users/me', userData),
  
  // 비밀번호 변경
  changePassword: (currentPassword: string, newPassword: string) => 
    api.put('/users/me/password', { currentPassword, newPassword }),
    
  // 내가 작성한 게시물 목록
  getMyPosts: (page = 1, limit = 10) => 
    api.get('/users/me/posts', { params: { page, limit } }),
    
  // 내가 작성한 댓글 목록
  getMyComments: (page = 1, limit = 10) => 
    api.get('/users/me/comments', { params: { page, limit } }),
    
  // 포인트 랭킹 조회
  getPointRanking: (limit = 5) => 
    api.get('/users/ranking/points', { params: { limit } }),
    
  // 포인트 내역 조회
  getPointHistory: (page = 1, limit = 20) => 
    api.get('/users/points/log', { params: { page, limit } }),

  // 사용자 활동 내역 조회
  getActivities: (page = 1, limit = 20) => 
    api.get('/users/activities', { params: { page, limit } }),

  // 최근 활동 내역 조회
  getRecentActivities: (limit = 10) => 
    api.get('/users/activities/recent', { params: { limit } })
};

// 파일 업로드 API
export const uploadAPI = {
  // 이미지 업로드
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

// 쪽지 인터페이스 정의
export interface MessageType {
  id: number;
  sender_id: number;
  receiver_id: number;
  title: string;
  content: string;
  is_read: number;
  created_at: string;
  read_at: string | null;
  sender_username?: string;
}

// 쪽지 API 함수
export const messageAPI = {
  // 내 쪽지 목록 조회
  getMyMessages: async (page = 1, limit = 10) => {
    return api.get('/users/messages', {
      params: { page, limit }
    }) as any;
  },
  
  // 특정 쪽지 조회 및 읽음 처리
  readMessage: async (id: number) => {
    return api.get(`/users/messages/${id}`) as any;
  },
  
  // 쪽지 삭제
  deleteMessage: async (id: number) => {
    return api.delete(`/users/messages/${id}`) as any;
  },
  
  // 안 읽은 쪽지 수 조회
  getUnreadCount: async () => {
    return api.get('/users/messages/unread') as any;
  }
};

// 문의 인터페이스 정의
export interface Inquiry {
  inquiry_id: number;
  user_id: number;
  title: string;
  content: string;
  status: 'pending' | 'answered' | 'closed';
  is_private: boolean;
  created_at: string;
  updated_at: string;
  username?: string;
  nickname?: string;
  reply_count?: number;
  last_reply_at?: string;
  replies?: InquiryReply[];
}

export interface InquiryReply {
  reply_id: number;
  inquiry_id: number;
  user_id: number;
  is_admin: boolean;
  content: string;
  created_at: string;
  username?: string;
  nickname?: string;
}

export interface InquiryStats {
  total: number;
  pending: number;
  answered: number;
  closed: number;
}

// 사용자 문의 관련 API
export const inquiryAPI = {
  // 내 문의 목록 조회
  getMyInquiries: (page = 1, limit = 10, status = 'all') => 
    api.get('/users/inquiries', { params: { page, limit, status } }),
  
  // 특정 문의 조회
  getInquiryById: (id: number) => 
    api.get(`/users/inquiries/${id}`),
  
  // 문의 작성
  createInquiry: (title: string, content: string, isPrivate = true) => 
    api.post('/users/inquiries', { title, content, is_private: isPrivate }),
  
  // 문의에 답변 작성
  addReply: (inquiryId: number, content: string) => 
    api.post(`/users/inquiries/${inquiryId}/replies`, { content }),
  
  // 문의 해결 완료로 표시
  closeInquiry: (inquiryId: number) => 
    api.put(`/users/inquiries/${inquiryId}/close`),
  
  // 문의 통계 조회
  getMyInquiryStats: () => 
    api.get('/users/inquiries-stats')
};

// 관리자 문의 관련 API
export const adminInquiryAPI = {
  // 모든 문의 목록 조회
  getAllInquiries: (page = 1, limit = 10, search = '', status = 'all') => 
    api.get('/admin/inquiries', { params: { page, limit, search, status } }),
  
  // 특정 문의 조회
  getInquiryById: (id: number) => 
    api.get(`/admin/inquiries/${id}`),
  
  // 문의에 답변 작성
  addReply: (inquiryId: number, content: string) => 
    api.post(`/admin/inquiries/${inquiryId}/replies`, { content }),
  
  // 문의 상태 변경
  updateStatus: (inquiryId: number, status: 'pending' | 'answered' | 'closed') => 
    api.put(`/admin/inquiries/${inquiryId}/status`, { status }),
  
  // 문의 통계 조회
  getInquiryStats: () => 
    api.get('/admin/inquiries-stats'),
  
  // 문의 테이블 생성
  createInquiryTables: () => 
    api.post('/admin/setup/inquiries-tables')
};

export default api;

export {
  apiConfig
}; 